import type { RiftboundCard } from '../types';
import { cardCollectorNumber } from '../../shared/cardCollectorNumber.js';
import { isPromoCard, isStandardSearchVariant } from './cardPrintVariant';

/** Gem tiers that have a `public/rarity-icons/*.webp` asset. */
export const RARITY_GEM_LABELS = ['Common', 'Uncommon', 'Rare', 'Epic', 'Showcase'] as const;
export type RarityGemLabel = (typeof RARITY_GEM_LABELS)[number];

const GEM_BY_SLUG = new Map(RARITY_GEM_LABELS.map((label) => [label.toLowerCase(), label]));

export type RarityCardRef = Pick<
  RiftboundCard,
  'rarity' | 'originRarity' | 'raw' | 'setCode' | 'cardType' | 'name' | 'number' | 'riftboundId' | 'signature' | 'supertype' | 'alternateArt'
>;

/** Title-case gem label, or null when the string is not a known rarity gem. */
export function normalizeRarityGemLabel(raw: string | null | undefined): RarityGemLabel | null {
  if (!raw) return null;
  return GEM_BY_SLUG.get(raw.trim().toLowerCase()) ?? null;
}

/** Origin-print gem rarity stamped on promo rows (`originRarity` or OpenRift raw). */
export function cardOriginRarity(card: Pick<RiftboundCard, 'originRarity' | 'raw'>): RarityGemLabel | null {
  const direct = normalizeRarityGemLabel(card.originRarity);
  if (direct) return direct;
  const raw = card.raw as Record<string, unknown> | undefined;
  const openrift = raw?.openrift as Record<string, unknown> | undefined;
  if (typeof openrift?.originRarity === 'string') {
    return normalizeRarityGemLabel(openrift.originRarity);
  }
  return null;
}

/**
 * Gem rarity to render for a card.
 *
 * Promo catalog rows store `rarity: 'Promo'`. Prefer the card's own gem, then
 * OpenRift `originRarity`, then the standard sibling printing in the same
 * print group (Kai'Sa OPP-247 → OGN Rare).
 */
export function cardRarityGemLabel(
  card: RarityCardRef,
  siblings?: readonly RarityCardRef[],
): RarityGemLabel | null {
  const own = normalizeRarityGemLabel(card.rarity);
  if (own) return own;
  const origin = cardOriginRarity(card);
  if (origin) return origin;
  if (!siblings?.length) return null;

  const standard = siblings.find(
    (sibling) => !isPromoCard(sibling) && isStandardSearchVariant(sibling),
  );
  const fromStandard = standard ? normalizeRarityGemLabel(standard.rarity) : null;
  if (fromStandard) return fromStandard;

  for (const sibling of siblings) {
    if (isPromoCard(sibling)) {
      const siblingOrigin = cardOriginRarity(sibling);
      if (siblingOrigin) return siblingOrigin;
      continue;
    }
    const gem = normalizeRarityGemLabel(sibling.rarity);
    if (gem) return gem;
  }
  return null;
}

/**
 * Prints the `· P` collector mark and the promo ribbon on the rarity gem.
 * Catalog promos (OPP / PR / JDG), plus the gallery copy of a promo rune
 * (`VEN-R04b`). Alt-art `a` runes are not promos.
 */
export function printsPromoMark(
  card: Pick<RiftboundCard, 'setCode' | 'rarity' | 'cardType' | 'number' | 'riftboundId'>,
): boolean {
  if (isPromoCard(card) || (card.rarity ?? '').trim().toLowerCase() === 'promo') return true;
  if ((card.cardType ?? '').toLowerCase() !== 'rune') return false;
  if ((card.rarity ?? '').trim().toLowerCase() !== 'showcase') return false;
  return /b$/i.test(cardCollectorNumber(card));
}

export function cardShowsPromoRarityCrown(
  card: Pick<RiftboundCard, 'setCode' | 'rarity' | 'cardType' | 'number' | 'riftboundId'>,
): boolean {
  return printsPromoMark(card);
}
